import { Suspense, useEffect, useMemo, useState } from 'react';
import { Await, useLoaderData, useOutlet } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { AppProvider } from '../../contexts/AppContext';
import { GeneratorDataContextProvider } from '../../pages/proposal-generator/GeneratorDataContext';
import Loading from '../utils/Loading';
import { properties } from '../../utils/Properties_es';
import { setAuthData } from '../../utils/LocalStorageManager';

const detectEmbedded = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('embedded') === '1';
  } catch {
    return false;
  }
};

const extractSsoFromHash = () => {
  try {
    const hash = window.location.hash || '';
    const match = hash.match(/(?:^#|&)sso=([^&]+)/);
    if (!match) return null;
    const encoded = decodeURIComponent(match[1]);
    const json = atob(encoded);
    const payload = JSON.parse(json);
    // Limpia el hash para que el token no quede expuesto en barra de
    // direcciones ni en navigation history.
    try {
      window.history.replaceState(
        null,
        '',
        window.location.pathname + window.location.search
      );
    } catch {}
    return payload;
  } catch {
    return null;
  }
};

const isAuthDataValid = (authData) => {
  if (!authData || !authData.tokenInfo || !authData.tokenInfo.access_token) {
    return false;
  }
  try {
    const decoded = jwtDecode(authData.tokenInfo.access_token);
    if (!decoded?.exp) return false;
    return Date.now() / 1000 < decoded.exp;
  } catch {
    return false;
  }
};

export const AuthLayout = () => {
  const outlet = useOutlet();
  const { authPromise } = useLoaderData();
  const isEmbedded = useMemo(() => detectEmbedded(), []);

  const [embeddedAuthData, setEmbeddedAuthData] = useState(null);
  const [waitingForSso, setWaitingForSso] = useState(isEmbedded);

  useEffect(() => {
    if (!isEmbedded) return undefined;

    let cancelled = false;

    // 1) Si el padre nos pasó el SSO via URL hash (#sso=<base64>), usarlo y
    //    persistirlo en localforage para futuros loads.
    const hashPayload = extractSsoFromHash();
    if (hashPayload && isAuthDataValid(hashPayload)) {
      (async () => {
        await setAuthData('authAdminInfo', hashPayload);
        if (cancelled) return;
        setEmbeddedAuthData(hashPayload);
        setWaitingForSso(false);
      })();
      return () => {
        cancelled = true;
      };
    }

    // 2) Sin hash: intentar reusar la sesión cacheada en localforage.
    (async () => {
      const existing = await authPromise;
      if (cancelled) return;
      if (isAuthDataValid(existing)) {
        setEmbeddedAuthData(existing);
        setWaitingForSso(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isEmbedded, authPromise]);

  const renderProviders = (authObject) => (
    <AppProvider authData={authObject} isEmbedded={isEmbedded}>
      <GeneratorDataContextProvider>{outlet}</GeneratorDataContextProvider>
    </AppProvider>
  );

  if (isEmbedded) {
    if (waitingForSso || !embeddedAuthData) {
      return <Loading title={properties.label_loading_title} />;
    }
    return (
      <Suspense fallback={<Loading title={properties.label_loading_title} />}>
        {renderProviders(embeddedAuthData)}
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<Loading title={properties.label_loading_title} />}>
      <Await resolve={authPromise} children={(authObject) => renderProviders(authObject)} />
    </Suspense>
  );
};
